import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BookHeart, Plus } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { Cover } from '@/components/cookbook/Pages'
import { FormError } from '@/components/FormError'
import { Steps } from '@/components/Steps'
import { ShareWith } from '@/components/ShareWith'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { useCookbooks } from '@/lib/cookbooks'
import type { Family, Space } from '@/lib/families'
import { cn } from '@/lib/utils'
import { audienceBody, startingAudience, useLastShare, type Audience } from '@/lib/spaces'

const link = 'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'

// Cookbooks: favourite recipes from any of the books, set out as pages to read or print. Each shows on the shelf as
// its own cover. With recipes to choose from and no cookbook yet, there's a quiet invitation to make one. With `mine`,
// only the cookbooks you made, and nothing at all if there are none.
export function CookbooksSection({
  family,
  space,
  hasRecipes,
  mine = false,
}: {
  family: Family
  space: Space | null
  hasRecipes: boolean
  mine?: boolean
}) {
  const cookbooks = useCookbooks(family.id, space?.id)
  const [starting, setStarting] = useState(false)
  const all = cookbooks.data?.cookbooks ?? []
  const list = mine ? all.filter((x) => x.mine) : all
  if (!cookbooks.data || (list.length === 0 && (mine || !hasRecipes))) return <FormError error={cookbooks.error} />
  return (
    <section className="flex flex-col gap-6" aria-labelledby="cookbooks">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 id="cookbooks" className="text-title">
          Cookbooks
        </h2>
        {list.length > 0 && (
          <Button size="lg" variant="outline" onClick={() => setStarting(true)}>
            <Plus aria-hidden /> New cookbook
          </Button>
        )}
      </div>
      {list.length === 0 ? (
        <div className="flex flex-col items-start gap-4 rounded-[2rem] border px-6 py-6 sm:flex-row sm:items-center sm:gap-6">
          <span className="grid size-14 shrink-0 place-items-center rounded-full bg-secondary">
            <BookHeart className="size-6" strokeWidth={1.6} aria-hidden />
          </span>
          <p className="flex-1 text-muted-foreground">
            Gather the recipes you love into a cookbook: a photo on one page, the recipe on the other. Read it here or
            print it.
          </p>
          <Button size="lg" onClick={() => setStarting(true)}>
            <Plus aria-hidden /> Make a cookbook
          </Button>
        </div>
      ) : (
        <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:grid-cols-5">
          {list.map((book) => (
            <li key={book.id}>
              <Link to={`/cookbook/${book.id}`} className={cn('group flex flex-col gap-3 rounded-md', link)}>
                <span className="block shadow-paper transition-[translate,box-shadow] duration-300 ease-settle group-hover:translate-y-[-3px] group-hover:shadow-lift">
                  <Cover title={book.title} subtitle="" picture={book.cover} sizes="(min-width: 1024px) 10rem, 25vw" />
                </span>
                <span className="flex flex-col gap-0.5 px-1">
                  <span className="truncate font-semibold">{book.title}</span>
                  <span className="truncate text-sub text-muted-foreground">
                    {book.recipes} {book.recipes === 1 ? 'recipe' : 'recipes'}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <NewCookbook
        key={`${space?.id}-${starting}`}
        open={starting}
        onOpenChange={setStarting}
        family={family}
        space={space}
      />
    </section>
  )
}

type Step = 'name' | 'who'

// Starting a cookbook like starting a book: what it's called, then who it's for. Then on to choosing its recipes.
// Started from a recipe book, it begins with that book's name and all its recipes, to trim and reorder.
export function NewCookbook({
  open,
  onOpenChange,
  family,
  space,
  name = '',
  recipeIds = [],
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  family: Family
  space: Space | null
  name?: string
  recipeIds?: string[]
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const steps: Step[] = ['name', 'who']
  const [step, setStep] = useState<Step>('name')
  const [title, setTitle] = useState(name)
  const [subtitle, setSubtitle] = useState('')
  const lastShare = useLastShare()
  const [who, setWho] = useState<Audience>(() => lastShare.start(startingAudience(family, space)))
  const at = steps.indexOf(step)
  const last = at === steps.length - 1
  const create = useMutation({
    mutationFn: async () => {
      const made = await api<{ id: string }>(`/families/${family.id}/cookbooks`, {
        method: 'POST',
        body: { title: title.trim(), subtitle: subtitle.trim(), ...audienceBody(who) },
      })
      if (recipeIds.length)
        await api(`/cookbooks/${made.id}/recipes`, { method: 'PUT', body: { recipeIds: recipeIds.slice(0, 300) } })
      return made
    },
    onSuccess: async ({ id }) => {
      lastShare.remember(who)
      await queryClient.invalidateQueries({ queryKey: ['cookbooks', family.id] })
      navigate(`/cookbook/${id}/edit`)
    },
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    if (last) create.mutate()
    else setStep(steps[at + 1]!)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={submit} className="flex flex-col gap-6">
          <DialogHeader>
            <p className="eyebrow">
              New cookbook
              {steps.length > 1 && ` · Step ${at + 1} of ${steps.length}`}
            </p>
            <DialogTitle>{step === 'name' ? 'What’s it called?' : 'Who’s this for?'}</DialogTitle>
            <DialogDescription>
              {step === 'name'
                ? 'It goes on the cover. You can change it any time.'
                : 'Only the people you pick will see it, and what’s in it. You can change this any time.'}
            </DialogDescription>
          </DialogHeader>
          {steps.length > 1 && <Steps n={steps.length} at={at} />}

          {step === 'name' && (
            <div className="grid gap-3">
              <Label htmlFor="cookbook-title">Cookbook name</Label>
              <Input
                id="cookbook-title"
                required
                maxLength={80}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <Label htmlFor="cookbook-subtitle" className="mt-2">
                A line under it <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="cookbook-subtitle"
                maxLength={120}
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
              />
            </div>
          )}

          {step === 'who' && (
            <ShareWith here={family.id} value={who} onChange={setWho} onlyMeHint="Just you, until you share it" />
          )}

          <FormError error={create.error} />
          <div className="flex flex-row-reverse flex-wrap justify-between gap-2">
            <Button type="submit" size="lg" disabled={create.isPending || (step === 'name' && !title.trim())}>
              {!last ? 'Next' : recipeIds.length ? 'Make the cookbook' : 'Choose recipes'}
            </Button>
            {at > 0 && (
              <Button type="button" variant="ghost" size="lg" onClick={() => setStep(steps[at - 1]!)}>
                Back
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
